/**
 * Collection Filters System - Moon Moon Agency v2.1
 * Shopify collection filtering with AJAX updates, custom events, and UI sync
 */

// Initialisation de l'objet queryParams pour stocker les paramètres de requête
Shopify.queryParams = {};
// Initialise les params depuis l'URL courante (conserve filtres actifs)
new URLSearchParams(window.location.search).forEach((value, key) => {
    Shopify.queryParams[key] = value;
});

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const COLLECTION_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,

	// Price Slider Settings
	priceGap: 10,
	
	// Selectors - Using data attributes for better separation of concerns
	selectors: {
		form: '[data-collection-form]',
		grid: '[data-collection-grid]',
		gridCollection: '[data-grid-collection]',
		shopifySection: '.shopify-section', // Keep as class (Shopify generated)
		activeFilters: '[data-active-filters]',
		reloadElements: '[data-collection-reload]',
		heroCount: '[data-hero-collection-count]',

		// Filter drawer (desktop + mobile unifies)
		filterDrawer: '[data-filter-drawer]',
		filterOverlay: '[data-filter-overlay]',
		filterPanel: '.mm-filter-panel',
		openFiltersDesktop: '[data-open-filters-desktop]',
		openFiltersMobile: '[data-open-filters-mobile]',
		filterClose: '[data-filter-close]',
		htmlElement: 'html',
		// Price sliders
		minRange: '[data-price-min]',
		maxRange: '[data-price-max]',
		sliderTrack: '[data-price-track]',
		minRangeValue: '[data-price-min-value]',
		maxRangeValue: '[data-price-max-value]',
		priceMinValue: '[data-price-min-display]',
		priceMaxValue: '[data-price-max-display]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Collection Event Emitter
const collectionEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'collection'
	};
	
	return document.dispatchEvent(new CustomEvent(`collection:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const CollectionDOM = {
	get form() { return document.querySelector(COLLECTION_CONFIG.selectors.form); },
	get grid() { return document.querySelector(COLLECTION_CONFIG.selectors.grid); },
	get gridCollection() { return document.querySelector(COLLECTION_CONFIG.selectors.gridCollection); },
	get html() { return document.querySelector(COLLECTION_CONFIG.selectors.htmlElement); },
	get filterDrawer() { return document.querySelector(COLLECTION_CONFIG.selectors.filterDrawer); },
	get filterPanel() { return document.querySelector(COLLECTION_CONFIG.selectors.filterPanel); },
	
	get activeFilters() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters); },
	get reloadElements() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements); },
	get heroCount() { return document.querySelector(COLLECTION_CONFIG.selectors.heroCount); },
	get formInputs() { return this.form?.querySelectorAll('input') || []; },
	
	// Price sliders
	get priceSliders() {
		return {
			minRange: document.querySelector(COLLECTION_CONFIG.selectors.minRange),
			maxRange: document.querySelector(COLLECTION_CONFIG.selectors.maxRange),
			sliderTrack: document.querySelector(COLLECTION_CONFIG.selectors.sliderTrack),
			minRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.minRangeValue),
			maxRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.maxRangeValue)
		};
	},
	
	get shopifySection() {
		const gridElem = this.gridCollection;
		return gridElem?.closest(COLLECTION_CONFIG.selectors.shopifySection);
	}
};

/*********************************************************************************************
 * 🔌 CORE COLLECTION API
 */
const CollectionAPI = {
	async request(url) {
		collectionEmit('api-start', { url });
		
		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			// Check if response is JSON (Section Rendering API) or HTML (fallback)
			const contentType = response.headers.get('content-type');
			const data = contentType && contentType.includes('application/json') 
				? await response.json() 
				: await response.text();
			
			collectionEmit('api-success', { url, responseType: typeof data });
			return data;
		} catch (error) {
			collectionEmit('api-error', { url, error: error.message });
			throw error;
		}
	},

	getSectionId() {
		const section = CollectionDOM.shopifySection;
		if (!section) {
			throw new Error('Collection grid element or shopify section not found');
		}
		return section.id;
	},

	getSectionIdShort() {
		return this.getSectionId().replace('shopify-section-', '');
	},

	async refreshWithFilters(url_filters) {
		collectionEmit('filter-start', { filters: url_filters });

		try {
			const currentUrl = `${window.location.pathname}`;
			const sectionId = this.getSectionIdShort();

			// Use Shopify Section Rendering API
			const requestUrl = `${currentUrl}?${url_filters}&sections=${sectionId}`;
			const response = await this.request(requestUrl);

			if (!CollectionDOM.grid) {
				throw new Error('Collection grid element not found');
			}

			// Handle different response types
			let newSectionHtml;
			if (typeof response === 'object' && response[sectionId]) {
				// Section Rendering API JSON response
				newSectionHtml = response[sectionId];
			} else if (typeof response === 'string') {
				// Fallback: HTML response
				const newPageEl = document.createElement('DIV');
				newPageEl.innerHTML = response;
				const newSection = newPageEl.querySelector(COLLECTION_CONFIG.selectors.grid);
				if (!newSection) {
					throw new Error('Required collection elements not found in response');
				}
				newSectionHtml = newSection.outerHTML;
			} else {
				throw new Error('Invalid response format');
			}

			this.updateDOM(newSectionHtml, currentUrl, url_filters);
			collectionEmit('filter-success', { filters: url_filters, url: `${currentUrl}?${url_filters}` });

		} catch (error) {
			collectionEmit('filter-error', { error: error.message, filters: url_filters });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-loading');
			}
			
			// Fallback: reload page if AJAX fails
			window.location.search = url_filters;
		}
	},

	updateDOM(newSectionHtml, currentUrl, url_filters) {
		// Create temporary element to parse new HTML
		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = newSectionHtml;

		// Update collection grid content (products only)
		const newGridCollection = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		if (newGridCollection && CollectionDOM.grid) {
			CollectionDOM.grid.innerHTML = newGridCollection.innerHTML;
		}

		// Update hero collection results count (section separate from the grid)
		if (newGridCollection && CollectionDOM.heroCount) {
			CollectionDOM.heroCount.textContent = newGridCollection.dataset.resultsCount;
		}

		// Update active filters
		const newActiveFilters = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters);
		CollectionDOM.activeFilters.forEach((currentFilter, index) => {
			if (newActiveFilters[index]) {
				currentFilter.innerHTML = newActiveFilters[index].innerHTML;
			}
		});

		// Update other elements with 'data-collection-reload' attribute
		const newReloadElems = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements);
		CollectionDOM.reloadElements.forEach((currentElem, index) => {
			if (newReloadElems[index]) {
				// Check if element has a <span> child (like buttons from mm-btn)
				const currentSpan = currentElem.querySelector('span');
				const newSpan = newReloadElems[index].querySelector('span');

				if (currentSpan && newSpan) {
					// Update only the text inside the span
					currentSpan.textContent = newSpan.textContent;
				} else {
					// Fallback: update entire innerHTML for simple elements
					currentElem.innerHTML = newReloadElems[index].innerHTML;
				}
			}
		});

		// Update URL in history without reloading page
		const newUrl = `${currentUrl}?${url_filters}`;
		window.history.replaceState(null, document.title, newUrl);
		
		if (CollectionDOM.grid) {
			CollectionDOM.grid.classList.remove('mm-loading');
		}
		
		// Re-initialize filters after DOM update
		CollectionFilters.init();
		
		// Re-initialize add to cart forms after DOM update
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}
	}
};

/*********************************************************************************************
 * 🔍 COLLECTION FILTERS MANAGEMENT
 */
const CollectionFilters = {
	init() {
		if (!CollectionDOM.form) {
			collectionEmit('form-error', { error: 'Collection facets form not found' });
			return;
		}
		
		const inputs = CollectionDOM.formInputs;
		
		// Add event listeners to inputs
		inputs.forEach(input => {
			// Remove existing listeners to avoid duplicates
			input.removeEventListener('change', this.handleInputChange);
			input.addEventListener('change', this.handleInputChange);
		});
		
		collectionEmit('filters-initialized', { inputCount: inputs.length });
	},

	handleInputChange(event) {
		collectionEmit('input-change', { 
			name: event.target.name || event.target.id,
			value: event.target.value,
			checked: event.target.checked 
		});
		
		CollectionFilters.submitForm();
	},

	async submitForm() {
		if (!CollectionDOM.form || !CollectionDOM.grid) {
			collectionEmit('form-error', { error: 'Required elements not found' });
			return;
		}
		
		try {
			collectionEmit('form-submit-start');
			
			const formData = new FormData(CollectionDOM.form);
			const url_filters = new URLSearchParams(formData).toString();
			
			CollectionDOM.grid.classList.add('mm-loading');
			
			await CollectionAPI.refreshWithFilters(url_filters);
			
			collectionEmit('form-submit-success', { filters: url_filters });
			
		} catch (error) {
			collectionEmit('form-submit-error', { error: error.message });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-loading');
			}
		}
	}
};

/*********************************************************************************************
 * 📱 MOBILE FILTERS
 */
const FILTER_FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const FilterDrawer = {
	_lastTrigger: null,

	get isOpen() {
		const drawer = CollectionDOM.filterDrawer;
		return drawer ? drawer.classList.contains('is-open') : false;
	},

	open(trigger = null) {
		const drawer = CollectionDOM.filterDrawer;
		const html = CollectionDOM.html;
		if (!drawer || !html) return;

		this._lastTrigger = trigger || document.activeElement;
		drawer.classList.add('is-open');
		drawer.setAttribute('aria-hidden', 'false');
		html.classList.add('stop-scrolling');
		this._focusFirst();
		collectionEmit('filters-opened');
	},

	close() {
		const drawer = CollectionDOM.filterDrawer;
		const html = CollectionDOM.html;
		if (!drawer || !html) return;

		drawer.classList.remove('is-open');
		drawer.setAttribute('aria-hidden', 'true');
		html.classList.remove('stop-scrolling');
		if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
			this._lastTrigger.focus();
		}
		collectionEmit('filters-closed');
	},

	_focusFirst() {
		const panel = CollectionDOM.filterPanel;
		const first = panel?.querySelector(FILTER_FOCUSABLE);
		if (first) first.focus();
	},

	_trapFocus(e) {
		if (e.key !== 'Tab' || !this.isOpen) return;
		const panel = CollectionDOM.filterPanel;
		if (!panel) return;

		const focusable = Array.from(panel.querySelectorAll(FILTER_FOCUSABLE))
			.filter((el) => el.offsetParent !== null);
		if (focusable.length === 0) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	}
};

/*********************************************************************************************
 * 💰 PRICE SLIDERS
 */
const PriceSliders = {
	get elements() {
		return CollectionDOM.priceSliders;
	},
	
	isReady() {
		const elements = this.elements;
		return elements.minRange && elements.maxRange && 
				elements.sliderTrack && elements.minRangeValue && 
				elements.maxRangeValue;
	},

	init() {
		if (!this.isReady()) return;
		
		const { minRange, maxRange } = this.elements;
		
		minRange.addEventListener('input', () => {
			if (parseInt(minRange.value) > parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap) {
				minRange.value = maxRange.value - COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		maxRange.addEventListener('input', () => {
			if (parseInt(maxRange.value) < parseInt(minRange.value) + COLLECTION_CONFIG.priceGap) {
				maxRange.value = parseInt(minRange.value) + COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});
		
		// Initial update
		this.updateRange();
		collectionEmit('price-sliders-initialized');
	},

	updateRange() {
		if (!this.isReady()) {
			collectionEmit('price-error', { error: 'Price slider elements not found' });
			return;
		}
		
		const { minRange, maxRange, sliderTrack, minRangeValue, maxRangeValue } = this.elements;
		
		const minValue = parseInt(minRange.value, 10); 
		const maxValue = parseInt(maxRange.value, 10);

		// Update min and max values display
		const minValueEl = minRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMinValue);
		const maxValueEl = maxRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMaxValue);
		
		if (minValueEl) minValueEl.innerHTML = minValue;
		if (maxValueEl) maxValueEl.innerHTML = maxValue;
		
		// Calculate percentages for track background
		const minPercent = (minValue / minRange.max) * 100;
		const maxPercent = (maxValue / maxRange.max) * 100;

		// Update track background to reflect selected price range
		sliderTrack.style.background = `linear-gradient(to right, #e5e5e5 ${minPercent}%, var(--black) ${minPercent}%, var(--black) ${maxPercent}%, #e5e5e5 ${maxPercent}%)`;

		collectionEmit('price-update', { minValue, maxValue, minPercent, maxPercent });
	}
};

/*********************************************************************************************
 * 🔄 SORT BY FUNCTIONALITY
 */
/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS (sort via inline onchange -> mmSortByChanged)
 */
window.mmSortByChanged = (select) => {
	if (!select) {
		collectionEmit('sort-error', { error: 'Sort by select not found' });
		return;
	}

	collectionEmit('sort-change', { value: select.value, previousValue: Shopify.queryParams.sort_by });
	Shopify.queryParams.sort_by = select.value;
	location.search = new URLSearchParams(Shopify.queryParams).toString();
};

// Filter drawer functions (desktop + mobile unifies)
window.mmOpenFilters = (trigger) => FilterDrawer.open(trigger);
window.mmCloseFilters = () => FilterDrawer.close();

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
    collectionEmit('init-start');

    try {
        CollectionFilters.init();
        PriceSliders.init();

        collectionEmit('init-success');
    } catch (error) {
        collectionEmit('init-error', { error: error.message });
        window.log('error', 'Collection', 'Collection initialization failed', { error: error.message });
    }
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
    // Filter close button
    const filterCloseBtn = document.querySelector(COLLECTION_CONFIG.selectors.filterClose);
    if (filterCloseBtn) {
        filterCloseBtn.addEventListener('click', () => FilterDrawer.close());
    }

    // Filter submit button
    const filterSubmitBtn = document.querySelector('[data-filter-submit]');
    if (filterSubmitBtn) {
        filterSubmitBtn.addEventListener('click', async () => {
            await CollectionFilters.submitForm();
            FilterDrawer.close();
        });
    }

    // Open filters buttons (desktop + mobile)
    const openSelectors = `${COLLECTION_CONFIG.selectors.openFiltersDesktop}, ${COLLECTION_CONFIG.selectors.openFiltersMobile}`;
    document.querySelectorAll(openSelectors).forEach((btn) => {
        btn.addEventListener('click', () => {
            if (typeof window.mmCloseMobileMenu === 'function') {
                window.mmCloseMobileMenu();
            }
            FilterDrawer.open(btn);
        });
    });

    // Overlay click closes the drawer
    const filterOverlay = document.querySelector(COLLECTION_CONFIG.selectors.filterOverlay);
    if (filterOverlay) {
        filterOverlay.addEventListener('click', () => FilterDrawer.close());
    }

    // Escape key + focus trap
    document.addEventListener('keydown', (e) => {
        if (!FilterDrawer.isOpen) return;
        if (e.key === 'Escape') {
            FilterDrawer.close();
        } else if (e.key === 'Tab') {
            FilterDrawer._trapFocus(e);
        }
    });
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	['init-success', 'filter-success', 'input-change', 'form-submit-start', 'price-update', 'sort-change'].forEach(event => {
		document.addEventListener(`collection:${event}`, e =>
			window.log('info', '🏷️ [COLLECTION]', event, e.detail)
		);
	});
}